import React, { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import {
  Button, Card, CardBody, Col, Container, Row,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, FormGroup, Table, Badge,
} from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../../common/breadcrumb";
import { errorToast } from "../../../shared/Toast";
import {
  listWaitlistStaff,
  createWaitlistStaff,
  updateWaitlistStaff,
  deleteWaitlistStaff,
  fetchBranches,
} from "../../../utils/apiCalls";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNo: "",
  password: "",
  branchIds: [],
  active: true,
};

const WaitlistStaff = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  // Create/edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Deactivate confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const branchOptions = useMemo(() => {
    if (!selectedMerchantId) return branches;
    return branches.filter((b) => String(b.MerchantId) === String(selectedMerchantId));
  }, [branches, selectedMerchantId]);

  const loadStaff = useCallback(async () => {
    if (!selectedMerchantId) { setStaff([]); return; }
    setIsFetching(true);
    const rows = await listWaitlistStaff(selectedMerchantId);
    setStaff(rows || []);
    setIsFetching(false);
  }, [selectedMerchantId]);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      setBranches(response?.data || []);
    };
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      firstName: row.firstName || "",
      lastName: row.lastName || "",
      email: row.email || "",
      phoneNo: row.phoneNo || "",
      password: "",
      branchIds: (row.assignedBranches || []).map((b) => b.id),
      active: !!row.active,
    });
    setModalOpen(true);
  };

  const toggleBranch = (id) => {
    setForm((f) => {
      const has = f.branchIds.includes(id);
      return { ...f, branchIds: has ? f.branchIds.filter((x) => x !== id) : [...f.branchIds, id] };
    });
  };

  const handleSubmit = async () => {
    if (!form.email.trim()) { errorToast("Email is required"); return; }
    if (!editingId && !form.password) { errorToast("Password is required"); return; }
    if (form.password && form.password.length < 8) { errorToast("Password must be at least 8 characters"); return; }
    if (form.branchIds.length === 0) { errorToast("Assign at least one branch"); return; }

    setSubmitting(true);
    const payload = {
      firstName: form.firstName.trim() || null,
      lastName: form.lastName.trim() || null,
      email: form.email.trim(),
      phoneNo: form.phoneNo.trim() || null,
      branchIds: form.branchIds,
      active: form.active,
      merchantId: selectedMerchantId,
    };
    if (form.password) payload.password = form.password;

    const result = editingId
      ? await updateWaitlistStaff(editingId, payload)
      : await createWaitlistStaff(payload);
    setSubmitting(false);

    if (result) {
      setModalOpen(false);
      loadStaff();
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmTarget) return;
    const ok = await deleteWaitlistStaff(confirmTarget.id, selectedMerchantId);
    if (ok) {
      setConfirmOpen(false);
      setConfirmTarget(null);
      loadStaff();
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Waitlist Staff" parent="Table Management" />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">
                    Waitlist Staff {staff.length > 0 && `(${staff.length})`}
                  </h5>
                  {selectedMerchantId && (
                    <Button color="primary" onClick={openCreate}>
                      Add Staff
                    </Button>
                  )}
                </div>

                {!selectedMerchantId ? (
                  <div className="text-center py-4">
                    <p>Please select a merchant from the header to view waitlist staff.</p>
                  </div>
                ) : isFetching ? (
                  <div className="text-center py-4"><p>Loading...</p></div>
                ) : (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Branches</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staff.length > 0 ? staff.map((u) => (
                        <tr key={u.id} style={{ verticalAlign: "middle" }}>
                          <td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</td>
                          <td>{u.email}</td>
                          <td>{u.phoneNo || "—"}</td>
                          <td>
                            {(u.assignedBranches || []).map((b) => (
                              <Badge key={b.id} color="info" pill className="me-1">{b.name}</Badge>
                            ))}
                          </td>
                          <td>
                            <Badge color={u.active ? "success" : "secondary"}>
                              {u.active ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : "—"}</td>
                          <td>
                            <i
                              className="fa fa-edit"
                              style={{ fontSize: 18, cursor: "pointer", color: "#3f51b5", marginRight: 12 }}
                              title="Edit"
                              onClick={() => openEdit(u)}
                            />
                            {u.active && (
                              <i
                                className="fa fa-trash"
                                style={{ fontSize: 18, cursor: "pointer", color: "rgb(220, 53, 69)" }}
                                title="Deactivate"
                                onClick={() => { setConfirmTarget(u); setConfirmOpen(true); }}
                              />
                            )}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className="text-center">No waitlist staff yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editingId ? "Edit Waitlist Staff" : "Add Waitlist Staff"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>First Name</Label>
                <Input
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Last Name</Label>
                <Input
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={form.email}
                  disabled={!!editingId}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Phone</Label>
                <Input
                  value={form.phoneNo}
                  onChange={(e) => setForm((f) => ({ ...f, phoneNo: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>{editingId ? "New Password (leave blank to keep)" : "Password *"}</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                />
              </FormGroup>
            </Col>
            {editingId && (
              <Col md="6">
                <FormGroup className="mt-4">
                  <Label className="d-flex align-items-center gap-2">
                    <Input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    />
                    <span className="ms-2">Active</span>
                  </Label>
                </FormGroup>
              </Col>
            )}
            <Col md="12">
              <FormGroup>
                <Label>Branches *</Label>
                <div style={{ border: "1px solid #dee2e6", borderRadius: 4, padding: 12, maxHeight: 240, overflowY: "auto" }}>
                  {branchOptions.length === 0 ? (
                    <p className="text-muted mb-0">No branches available.</p>
                  ) : branchOptions.map((b) => (
                    <FormGroup check inline key={b.id} className="mb-2 me-3">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={form.branchIds.includes(b.id)}
                          onChange={() => toggleBranch(b.id)}
                        />{" "}
                        {b.name}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : editingId ? "Save" : "Create"}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)}>
        <ModalHeader toggle={() => setConfirmOpen(false)}>Confirm Deactivate</ModalHeader>
        <ModalBody>
          Deactivate <strong>{confirmTarget?.email}</strong>? They will no longer be able to log in.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleConfirmDeactivate}>Deactivate</Button>
          <Button color="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default WaitlistStaff;
